import React, { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Divider,
  Link,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ArrowForward,
  OpenInNew,
  ArrowBack,
  AccessTime,
  CalendarToday,
  Person,
  LinkedIn,
} from '@mui/icons-material';
import ArticleTable from '../../components/Blog/ArticleTable';
import SEOHead from '../../components/SEO/SEOHead';
import BreadcrumbNav from '../../components/SEO/BreadcrumbNav';
import AtomicAnswer from '../../components/SEO/AtomicAnswer';
import FAQSection from '../../components/SEO/FAQSection';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import carlosPhoto from '../../assets/img/author-carlos.jpg';
import { useTranslation } from 'react-i18next';
import { REFERRALS } from '../../components/shared/ReferralCTA';

const ConstruirCreditoPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();
  const [takeawaysOpen, setTakeawaysOpen] = useState(false);

  const HERO_IMAGE = '/images/blog/construir-credito-canada-tarjeta.jpg';
  const TAGS = [t('blog.construirCredito.tag1'), t('blog.construirCredito.tag2')];
  const AUTHOR = { name: 'Carlos Alviz', role: t('blog.construirCredito.authorRole'), linkedin: 'https://www.linkedin.com/in/alvizcarlos/', photo: carlosPhoto };

  const articleSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: t('blog.construirCredito.schemaHeadline'),
      description: t('blog.construirCredito.seoDescription'),
      datePublished: '2026-02-24',
      dateModified: '2026-02-24',
      author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
      publisher: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com', logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' } },
      mainEntityOfPage: 'https://www.finmaple.com/blog/construir-credito-canada-desde-cero',
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.article-headline', '.atomic-answer'],
      },
      inLanguage: i18n.language,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: t('blog.construirCredito.howToName'),
      description: t('blog.construirCredito.howToDescription'),
      totalTime: 'P90D',
      inLanguage: i18n.language,
      step: [
        {
          '@type': 'HowToStep',
          name: t('blog.construirCredito.howToStep1Name'),
          text: t('blog.construirCredito.howToStep1Text'),
          position: 1,
        },
        {
          '@type': 'HowToStep',
          name: t('blog.construirCredito.howToStep2Name'),
          text: t('blog.construirCredito.howToStep2Text'),
          position: 2,
        },
        {
          '@type': 'HowToStep',
          name: t('blog.construirCredito.howToStep3Name'),
          text: t('blog.construirCredito.howToStep3Text'),
          position: 3,
        },
        {
          '@type': 'HowToStep',
          name: t('blog.construirCredito.howToStep4Name'),
          text: t('blog.construirCredito.howToStep4Text'),
          position: 4,
        },
        {
          '@type': 'HowToStep',
          name: t('blog.construirCredito.howToStep5Name'),
          text: t('blog.construirCredito.howToStep5Text'),
          position: 5,
        },
        {
          '@type': 'HowToStep',
          name: t('blog.construirCredito.howToStep6Name'),
          text: t('blog.construirCredito.howToStep6Text'),
          position: 6,
        },
        {
          '@type': 'HowToStep',
          name: t('blog.construirCredito.howToStep7Name'),
          text: t('blog.construirCredito.howToStep7Text'),
          position: 7,
        },
        {
          '@type': 'HowToStep',
          name: t('blog.construirCredito.howToStep8Name'),
          text: t('blog.construirCredito.howToStep8Text'),
          position: 8,
        },
      ],
    },
  ];

  const SECURED_CARDS = [
    {
      name: 'Home Trust Secured Visa',
      deposit: t('blog.construirCredito.securedCards.card1Deposit'),
      fee: '$0',
      reporta: t('blog.construirCredito.securedCards.card1Reports'),
      ideal: t('blog.construirCredito.securedCards.card1Ideal'),
    },
    {
      name: 'Capital One Guaranteed Secured',
      deposit: '$75 – $300',
      fee: t('blog.construirCredito.securedCards.card2Fee'),
      reporta: 'TransUnion',
      ideal: t('blog.construirCredito.securedCards.card2Ideal'),
    },
    {
      name: 'Neo Secured Mastercard',
      deposit: t('blog.construirCredito.securedCards.card3Deposit'),
      fee: '$96/yr',
      reporta: 'TransUnion',
      ideal: t('blog.construirCredito.securedCards.card3Ideal'),
    },
  ];

  const FAQS = [
    {
      question: t('blog.construirCredito.faq1Q'),
      answer: t('blog.construirCredito.faq1A'),
    },
    {
      question: t('blog.construirCredito.faq2Q'),
      answer: t('blog.construirCredito.faq2A'),
    },
    {
      question: t('blog.construirCredito.faq3Q'),
      answer: t('blog.construirCredito.faq3A'),
    },
    {
      question: t('blog.construirCredito.faq4Q'),
      answer: t('blog.construirCredito.faq4A'),
    },
    {
      question: t('blog.construirCredito.faq5Q'),
      answer: t('blog.construirCredito.faq5A'),
    },
  ];

  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={t('blog.construirCredito.seoTitle')}
        description={t('blog.construirCredito.seoDescription')}
        canonical="/blog/construir-credito-canada-desde-cero"
        routeKey="blogCredito"
        seoKey="blogCredito"
        noIndex={i18n.language !== 'es'}
        ogImage="https://www.finmaple.com/images/blog/construir-credito-canada-tarjeta.jpg"
        schema={articleSchema}
      />

      {/* Hero Image */}
      <Box
        role="img"
        aria-label={t('blog.construirCredito.heroAlt')}
        sx={{
          width: '100%',
          height: { xs: 220, md: 360 },
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.45)), url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Container maxWidth="md" sx={{ pb: 4 }}>
          <Button
            component={RouterLink}
            to={`/${lng}/blog`}
            startIcon={<ArrowBack />}
            sx={{ color: '#fff', textTransform: 'none', mb: 2, fontWeight: 500 }}
          >
            {t('blog.construirCredito.backToGuides')}
          </Button>
        </Container>
      </Box>

      <TrustStrip />

      <Container maxWidth="md" sx={{ mt: 0, position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            px: { xs: 3, md: 5 },
            py: { xs: 3, md: 4 },
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}
        >
          <BreadcrumbNav items={[{ label: t('blog.construirCredito.breadcrumbGuides'), to: `/${lng}/blog` }, { label: t('blog.construirCredito.breadcrumb') }]} />

          {/* Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {TAGS.map((tag, i) => (
              <Chip key={i} label={tag} size="small" sx={{ bgcolor: '#F0F2F5', color: '#5F6B7A', fontWeight: 500, fontSize: '0.8rem' }} />
            ))}
          </Box>

          {/* Title */}
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.2, mb: 3, fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            {t('blog.construirCredito.pageTitle')}
          </Typography>

          {/* Meta */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, color: '#5F6B7A', mb: 3, fontSize: '0.9rem' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
              <a href={AUTHOR.linkedin} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1E3A5F', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A5F' }}>{AUTHOR.name}</Typography>
                <LinkedIn sx={{ fontSize: 16, color: '#1E3A5F' }} />
              </a>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t('blog.construirCredito.date')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16 }} />
              <Typography variant="body2">{t('blog.construirCredito.readTime')}</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Atomic Answer */}
          <AtomicAnswer text={t('blog.construirCredito.atomicAnswer')} />

          {/* Key Takeaways - Collapsible */}
          <Box
            sx={{
              border: '1px solid #EEF0F3',
              borderRadius: '10px',
              overflow: 'hidden',
              mt: 5,
              mb: 5,
            }}
          >
            <Box
              onClick={() => setTakeawaysOpen(!takeawaysOpen)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 1.5,
                bgcolor: '#F7F9FC',
                cursor: 'pointer',
                '&:hover': { bgcolor: '#EEEEEE' },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                {t('blog.construirCredito.takeawaysTitle')}
              </Typography>
              {takeawaysOpen ? <ExpandLess sx={{ color: '#5F6B7A' }} /> : <ExpandMore sx={{ color: '#5F6B7A' }} />}
            </Box>
            <Box component="ul" sx={{ display: takeawaysOpen ? 'block' : 'none', px: 3, py: 2, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.6 } }}>
                <li>{t('blog.construirCredito.takeaway1')}</li>
                <li>{t('blog.construirCredito.takeaway2')}</li>
                <li>{t('blog.construirCredito.takeaway3')}</li>
                <li>{t('blog.construirCredito.takeaway4')}</li>
                <li>{t('blog.construirCredito.takeaway5')}</li>
              </Box>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Qué significa "no tener historial" */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 6, mb: 3 }}>
            {t('blog.construirCredito.section1Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2, '& strong': { fontWeight: 600 }, '& em': { fontStyle: 'italic' } }} dangerouslySetInnerHTML={{ __html: t('blog.construirCredito.section1P1') }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.construirCredito.section1P2a')}
            <strong>{t('blog.construirCredito.section1P2Strong')}</strong>
            {t('blog.construirCredito.section1P2b')}
            <RouterLink to={`/${lng}/blog/costo-de-vida-montreal-2026`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t('blog.construirCredito.section1Link1')}</RouterLink>
            {t('blog.construirCredito.section1P2c')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.construirCredito.section1P3')}
          </Typography>

          {/* Info box */}
          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t('blog.construirCredito.infoBox1Label')}</strong> {t('blog.construirCredito.infoBox1')}
            </Typography>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Cómo se crea tu reporte */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.construirCredito.section2Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.construirCredito.section2P1')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2, '& strong': { fontWeight: 600 } }} dangerouslySetInnerHTML={{ __html: t('blog.construirCredito.section2P2') }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.construirCredito.section2P3')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>{t('blog.construirCredito.scoreFactors.paymentLabel')}</strong> {t('blog.construirCredito.scoreFactors.paymentText')}</li>
            <li><strong>{t('blog.construirCredito.scoreFactors.utilizationLabel')}</strong> {t('blog.construirCredito.scoreFactors.utilizationText')}</li>
            <li><strong>{t('blog.construirCredito.scoreFactors.ageLabel')}</strong> {t('blog.construirCredito.scoreFactors.ageText')}</li>
            <li><strong>{t('blog.construirCredito.scoreFactors.typesLabel')}</strong> {t('blog.construirCredito.scoreFactors.typesText')}</li>
            <li><strong>{t('blog.construirCredito.scoreFactors.inquiriesLabel')}</strong> {t('blog.construirCredito.scoreFactors.inquiriesText')}</li>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Plan de 90 días */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.construirCredito.section3Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t('blog.construirCredito.section3Intro')}
          </Typography>

          {/* Día 1-7 */}
          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>
              {t('blog.construirCredito.plan.day1to7Title')}
            </Typography>
            <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
              <li><strong>{t('blog.construirCredito.plan.day1to7Step1Bold')}</strong> {t('blog.construirCredito.plan.day1to7Step1Text')}</li>
              <li><strong>{t('blog.construirCredito.plan.day1to7Step2Bold')}</strong> {t('blog.construirCredito.plan.day1to7Step2TextA')}<RouterLink to={`/${lng}/blog/mejores-cuentas-sin-fees-recien-llegados`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t('blog.construirCredito.plan.day1to7Step2Link')}</RouterLink>{t('blog.construirCredito.plan.day1to7Step2TextB')}{' '}
              <a
                href={REFERRALS.wealthsimple.href}
                target="_blank"
                rel="noopener noreferrer sponsored"
                style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}
                onClick={() => { if (window.plausible) window.plausible('Referral Click', { props: { bank: 'wealthsimple' } }); }}
              >Wealthsimple</a>
              {' '}{t('blog.construirCredito.plan.day1to7Step2TextC')}</li>
              <li><strong>{t('blog.construirCredito.plan.day1to7Step3Bold')}</strong> {t('blog.construirCredito.plan.day1to7Step3Text')}</li>
            </Box>
          </Box>

          {/* Día 7-30 */}
          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>
              {t('blog.construirCredito.plan.day7to30Title')}
            </Typography>
            <Box component="ol" start={4} sx={{ pl: 2.5, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
              <li><strong>{t('blog.construirCredito.plan.day7to30Step4Bold')}</strong> {t('blog.construirCredito.plan.day7to30Step4Text')}</li>
              <li><strong>{t('blog.construirCredito.plan.day7to30Step5Bold')}</strong> {t('blog.construirCredito.plan.day7to30Step5Text')}</li>
              <li><strong>{t('blog.construirCredito.plan.day7to30Step6Bold')}</strong> {t('blog.construirCredito.plan.day7to30Step6Text')}</li>
            </Box>
          </Box>

          {/* Día 30-60 */}
          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>
              {t('blog.construirCredito.plan.day30to60Title')}
            </Typography>
            <Box component="ol" start={7} sx={{ pl: 2.5, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
              <li><strong>{t('blog.construirCredito.plan.day30to60Step7Bold')}</strong> {t('blog.construirCredito.plan.day30to60Step7Text')}</li>
              <li><strong>{t('blog.construirCredito.plan.day30to60Step8Bold')}</strong> {t('blog.construirCredito.plan.day30to60Step8Text')}</li>
              <li><strong>{t('blog.construirCredito.plan.day30to60Step9Bold')}</strong> {t('blog.construirCredito.plan.day30to60Step9Text')}</li>
            </Box>
          </Box>

          {/* Día 60-90 */}
          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>
              {t('blog.construirCredito.plan.day60to90Title')}
            </Typography>
            <Box component="ol" start={10} sx={{ pl: 2.5, m: 0, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
              <li><strong>{t('blog.construirCredito.plan.day60to90Step10Bold')}</strong> {t('blog.construirCredito.plan.day60to90Step10Text')}</li>
              <li><strong>{t('blog.construirCredito.plan.day60to90Step11Bold')}</strong> {t('blog.construirCredito.plan.day60to90Step11Text')}</li>
              <li><strong>{t('blog.construirCredito.plan.day60to90Step12Bold')}</strong> {t('blog.construirCredito.plan.day60to90Step12TextA')}<RouterLink to={`/${lng}/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t('blog.construirCredito.plan.day60to90Step12Link')}</RouterLink>{t('blog.construirCredito.plan.day60to90Step12TextB')}</li>
            </Box>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Error #1: utilización */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.construirCredito.section4Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2, '& strong': { fontWeight: 600 } }} dangerouslySetInnerHTML={{ __html: t('blog.construirCredito.section4P1') }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2, '& strong': { fontWeight: 600 } }} dangerouslySetInnerHTML={{ __html: t('blog.construirCredito.section4P2') }} />

          {/* Warning box */}
          <Box sx={{ bgcolor: '#FFFBF0', borderLeft: '4px solid #F9A825', p: 3, borderRadius: '0 8px 8px 0', mb: 2.5 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t('blog.construirCredito.section4WarningLabel')}</strong> {t('blog.construirCredito.section4Warning')}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2, '& strong': { fontWeight: 600 }, '& em': { fontStyle: 'italic' } }} dangerouslySetInnerHTML={{ __html: t('blog.construirCredito.section4P3') }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t('blog.construirCredito.section4P4')}
          </Typography>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Revisar reporte y corregir errores */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.construirCredito.section5Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2, '& strong': { fontWeight: 600 } }} dangerouslySetInnerHTML={{ __html: t('blog.construirCredito.section5P1') }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.construirCredito.section5P2')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1.5, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li><strong>Equifax:</strong> {t('blog.construirCredito.section5Equifax')}</li>
            <li><strong>TransUnion:</strong> {t('blog.construirCredito.section5TransUnion')}</li>
            <li><strong>Borrowell:</strong> {t('blog.construirCredito.section5Borrowell')}</li>
          </Box>

          {/* Borrowell CTA */}
          <Box
            sx={{
              bgcolor: '#F7F9FC',
              border: '1px solid #E6E9EE',
              borderRadius: '16px',
              p: 3,
              mb: 3,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.25 }}>
                {t('blog.construirCredito.borrowellCtaTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6 }}>
                {t('blog.construirCredito.borrowellCtaDesc')}
              </Typography>
            </Box>
            <Button
              href={REFERRALS.borrowell.href}
              target="_blank"
              rel="noopener noreferrer sponsored"
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={() => { if (window.plausible) window.plausible('Referral Click', { props: { bank: 'borrowell' } }); }}
              sx={{
                bgcolor: '#1E3A5F',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                px: 3,
                flexShrink: 0,
                '&:hover': { bgcolor: '#1A3152' },
              }}
            >
              {t('blog.construirCredito.borrowellCtaButton')}
            </Button>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            {t('blog.construirCredito.section5P3')}
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: '#1A1A1A', lineHeight: 1.7 } }}>
            <li>{t('blog.construirCredito.section5Check1')}</li>
            <li>{t('blog.construirCredito.section5Check2')}</li>
            <li>{t('blog.construirCredito.section5Check3')}</li>
            <li>{t('blog.construirCredito.section5Check4')}</li>
            <li>{t('blog.construirCredito.section5Check5')}</li>
            <li>{t('blog.construirCredito.section5Check6')}</li>
          </Box>

          {/* Warning box */}
          <Box sx={{ bgcolor: '#FFFBF0', borderLeft: '4px solid #F9A825', p: 3, borderRadius: '0 8px 8px 0', mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t('blog.construirCredito.section5WarningLabel')}</strong> {t('blog.construirCredito.section5Warning')}
            </Typography>
          </Box>

          {/* ───────────────────────────────────────────────────────── */}
          {/* H2: Tarjeta asegurada vs. newcomer */}
          {/* ───────────────────────────────────────────────────────── */}
          <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mt: 5, mb: 3 }}>
            {t('blog.construirCredito.section6Title')}
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2, '& strong': { fontWeight: 600 } }} dangerouslySetInnerHTML={{ __html: t('blog.construirCredito.section6P1') }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2, '& strong': { fontWeight: 600 } }} dangerouslySetInnerHTML={{ __html: t('blog.construirCredito.section6P2') }} />
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 3 }}>
            {t('blog.construirCredito.section6P3a')}
            <RouterLink to={`/${lng}/blog/tarjetas-newcomers-que-piden-bancos`} style={{ color: '#1E3A5F', fontWeight: 600, textDecoration: 'underline' }}>{t('blog.construirCredito.section6Link1')}</RouterLink>
            {t('blog.construirCredito.section6P3b')}
          </Typography>

          {/* Comparison Table */}
          <ArticleTable
            columns={[
              { key: 'name', label: t('blog.construirCredito.tableCol1') },
              { key: 'deposit', label: t('blog.construirCredito.tableCol2') },
              { key: 'fee', label: t('blog.construirCredito.tableCol3') },
              { key: 'reporta', label: t('blog.construirCredito.tableCol4') },
              { key: 'ideal', label: t('blog.construirCredito.tableCol5') },
            ]}
            rows={SECURED_CARDS.map((row) => ({
              ...row,
              ideal: { text: row.ideal, sx: { color: '#5F6B7A' } },
            }))}
          />

          {/* Info box */}
          <Box sx={{ bgcolor: '#F7F9FC', borderLeft: '4px solid #1E3A5F', p: 3, borderRadius: '0 8px 8px 0', mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#1A1A1A', lineHeight: 1.7 }}>
              <strong>{t('blog.construirCredito.newcomerProgramsLabel')}</strong> {t('blog.construirCredito.newcomerPrograms')}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            <strong>{t('blog.construirCredito.recommendationLabel')}</strong> {t('blog.construirCredito.recommendation')}
          </Typography>

          {/* ───────────────────────────────────────────────────────── */}
          {/* Experience Panel */}
          {/* ───────────────────────────────────────────────────────── */}
          <Box sx={{ bgcolor: '#F0FAF1', borderLeft: '4px solid #2E7D32', p: 3, borderRadius: '0 8px 8px 0', my: 4 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>
              {t('blog.construirCredito.experienceTitle')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, fontStyle: 'italic' }}>
              {t('blog.construirCredito.experienceText')}
            </Typography>
          </Box>

          {/* Mid-article CTA */}
          <Box sx={{ bgcolor: '#F7F9FC', border: '1px solid #E6E9EE', borderRadius: '12px', p: 3, mt: 4, mb: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '16px', color: '#1A1A1A', mb: 2, lineHeight: 1.6 }}>
              {t('blog.construirCredito.ctaQuestion')}
            </Typography>
            <Button component={RouterLink} to={`/${lng}/tarjetas-de-credito`} variant="contained" endIcon={<ArrowForward />}
              sx={{ bgcolor: '#1E3A5F', textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 3, py: 1, '&:hover': { bgcolor: '#1A3152' } }}>
              {t('blog.construirCredito.ctaButton')}
            </Button>
          </Box>

          <Divider sx={{ mt: 5, mb: 0 }} />

          {/* FAQ Section */}
          <FAQSection faqs={FAQS} />


          {/* Interlink CTA Box */}
          <Box
            sx={{
              bgcolor: '#1E3A5F',
              borderRadius: '10px',
              p: 3,
              mt: 4,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                {t('blog.construirCredito.interlinkTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                {t('blog.construirCredito.interlinkSubtitle')}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/${lng}/tarjetas-de-credito`}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{
                bgcolor: '#fff',
                color: '#1E3A5F',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '14px',
                px: 3,
                flexShrink: 0,
                '&:hover': { bgcolor: '#E8EAF6' },
              }}
            >
              {t('blog.construirCredito.interlinkButton')}
            </Button>
          </Box>

          {/* Official Sources */}
          <Box sx={{ mt: 5, pt: 3, borderTop: '1px solid #E6E9EE' }}>
            <Typography variant="subtitle2" sx={{ color: '#5F6B7A', fontWeight: 600, mb: 1.5 }}>
              {t('blog.construirCredito.officialSources')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Link href="https://www.canada.ca/en/financial-consumer-agency/services/credit-reports-score.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('blog.construirCredito.sourceLabel1')} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.equifax.ca" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('blog.construirCredito.sourceLabel2')} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.transunion.ca" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('blog.construirCredito.sourceLabel3')} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
              <Link href="https://www.canada.ca/en/employment-social-development/services/sin.html" target="_blank" rel="noopener noreferrer" sx={{ color: '#1E3A5F', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('blog.construirCredito.sourceLabel4')} <OpenInNew sx={{ fontSize: 14 }} />
              </Link>
            </Box>
            <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 2 }}>
              {t('blog.construirCredito.footer')}
            </Typography>
          </Box>

          {/* Author Bio */}
          <Divider sx={{ mt: 5, mb: 0 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 3, bgcolor: '#F7F9FC', borderRadius: '12px', mt: 3 }}>
            <Box component="img" src={AUTHOR.photo} alt={AUTHOR.name} loading="lazy" sx={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>{AUTHOR.name}</Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A' }}>{AUTHOR.role}</Typography>
            </Box>
            <Button href={AUTHOR.linkedin} target="_blank" rel="noopener noreferrer" startIcon={<LinkedIn />} size="small"
              sx={{ textTransform: 'none', color: '#1E3A5F', fontWeight: 600, borderColor: '#1E3A5F', '&:hover': { bgcolor: 'rgba(10,102,194,0.08)' } }}
              variant="outlined">
              LinkedIn
            </Button>
          </Box>
        </Box>

        <RelatedArticles slug="construir-credito-canada-desde-cero" />
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default ConstruirCreditoPage;
